import { clsx } from 'clsx';
import { Select } from 'antd';
import { useNotify } from 'react-admin';
import { useListContext } from 'ra-core';
import { useModalContext } from '@providers';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { useRequest } from '@hooks';
import { stateHttp } from '@network';
import { StateBulkPropsTitles, StatePossiblePhenomena, statePossiblePhenomena, stateScoreByName } from '@types';
import { BlueButton, BlueOutlineButton } from '@components';
import cls from './StateListActions.module.css';

type BulkProp = keyof typeof StateBulkPropsTitles;
const bulkPropsList = Object.keys(StateBulkPropsTitles)
  .filter(k => isNaN(+k) || isNaN(parseFloat(k)));

export const StateListActions = () => {
  const notify = useNotify();
  const { hideModal } = useModalContext();
  const { selectedIds, refetch } = useListContext();

  const [prop, setProp] = useState<BulkProp>();
  const [value, setValue] = useState<string | number | null>(null);
  const [possiblePhenomena, setPossiblePhenomena]
    = useState<StatePossiblePhenomena[] | string[] | string>([]);

  const resetValues = () => {
    setValue(null);
  };

  const onCancel = () => {
    setProp(undefined);
    resetValues();
    hideModal();
  };

  const scoreChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.replace(/\D/g, '');

    const newValue = value === '' || value === null
      ? null
      : +value <= 100
        ? +value
        : 100;

    setValue(newValue);
  };

  const { loading, submit } = useRequest(async () => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return notify('No records are selected');
    }
    if (!prop) {
      return notify('No property is selected');
    }

    const payload = prop === 'Possible Phenomena' ? possiblePhenomena as string[] : value as number;
    const updatedTotal = await stateHttp.updateBulk({
      selectedIds,
      prop: stateScoreByName[prop],
      value: payload,
    });
    if (selectedIds.length !== updatedTotal) {
      notify(`Updated: ${updatedTotal} of ${selectedIds.length} records. Some records can't be changed.`);
    } else {
      notify(`Updated records: ${updatedTotal}`);
    }

    onCancel();
    refetch();
  });

  const bulkPropsSorted = bulkPropsList
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  useEffect(() => {
    resetValues();
  }, [prop]);

  return (
    <div className={cls.box}>
      <div className={cls.title}>Properties to update</div>
      <Select
        value={prop}
        onChange={v => setProp(v)}
        className={cls.select}
        placeholder="Select a property to edit">
        {bulkPropsSorted.map(propName => (
          <Select.Option key={propName} value={propName}>{propName}</Select.Option>
        ))}
      </Select>

      <div className={cls.content}>
        <div className={cls.title}>
          <div>{prop}</div>
        </div>
        {prop === 'Possible Phenomena' ? (
          <Select
            value={possiblePhenomena}
            onChange={e => setPossiblePhenomena(e)}
            mode="multiple"
            className={cls.select}>
            {statePossiblePhenomena.map(n => (
              <Select.Option key={n} value={n}>{n}</Select.Option>
            ))}
          </Select>
        ) : null}
        {prop && prop !== 'Possible Phenomena' ? (
          <input
            className={clsx(cls.value, cls.fullWidth)}
            name={prop}
            value={value === null ? '' : value}
            onChange={scoreChangeHandler}
          />
        ) : null}
      </div>

      <div className={cls.controls}>
        <BlueButton className={cls.btn} onClick={submit} disabled={loading}>Update</BlueButton>
        <BlueOutlineButton className={cls.btn} onClick={onCancel}>Cancel</BlueOutlineButton>
      </div>
    </div>
  );
};
